// Owner console API client (`/api/system`, the console origin; API_CONTRACTS §1.1, §3.33).
// Sign-in (SYSTEM-001 platform realm, `__Host-ady_p`): Google (GET /auth/google/start, a browser
// navigation) or email OTP (/auth/otp/{start,verify}) gives a pending session; a second factor
// (/auth/mfa/totp or /auth/mfa/recovery) completes it. There are no platform passkey routes.
import type {
  AdyGateway,
  ErrorBody,
  ErrorCode,
  ImportBoard,
  ImportOrder,
  ImportOrderState,
  ImportPayMethod,
  ImportProduct,
  ImportQuote,
  AdsBoard,
  AdsInsightDay,
  AdsPayDetails,
  AdsPlatform,
  AdsSettings,
  BillingRules,
  ConsoleOverview,
  HealthBoard,
  IncidentView,
  ShopIntegrations,
  StorefrontBoard,
  SwitchBoard,
  SwitchRules,
  ThemeCatalogBoard,
  ThemeDeleteResult,
  CoursesBoard,
  InvoiceBoard,
  InvoiceView,
  PlanCouponBody,
  PlanCouponView,
  ReferralBoard,
  ReferralSettings,
  RevenueReport,
  ShopsReport,
  SubscriptionsBoard,
  DeskBoard,
  DeskPriority,
  DeskState,
  DeskTicket,
  IdeaBoard,
  IdeaStatus,
  OnboardingBoard,
  ReleaseNoteView,
  ReleaseTag,
  SetupStep,
  PlanIcon,
  PlatformNoticeView,
  PricingBoard,
  SmsBoard,
  VendorDetail,
  VendorDirectory,
  SmsPack,
  SmsPayDetails,
  WalletActivity,
  WalletBoard,
  AuditPage,
  JobFailureView,
  LegalBoard,
  LegalKind,
  ReportBoard,
  ReportState,
  StaffRole,
  TeamBoard,
  UsageBoard,
} from "@ady/contracts";
import { i18n, t } from "@ady/i18n";

export const API_BASE = "/api/system";

export interface StaffMe {
  staff: {
    id: string;
    email: string;
    display_name: string;
    role: "owner" | "operator" | "support" | "reviewer";
  };
  mfa: { totp: boolean; passkeys: number };
  environment: "local" | "staging" | "production";
}
export interface PlatformVerifyResponse {
  /** Always true for platform accounts: MFA is mandatory at sign-in. */
  mfa_required: true;
  /** False when the account has no factor yet: the workspace sets up an authenticator (D-081). */
  mfa_enrolled: boolean;
}
/** D-081: an authenticator app being set up; the URI (and so the secret) is returned once. */
export interface AuthenticatorEnrolment {
  factor_id: string;
  otpauth_uri: string;
}
/** D-081: My security. */
export interface StaffAuthenticators {
  authenticators: {
    id: string;
    label: string;
    added_at: string | null;
    last_used_at: string | null;
  }[];
  passkeys: number;
  recovery_codes_remaining: number;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ErrorCode | "network",
    readonly requestId?: string,
    readonly retryAfterS?: number,
    /** The server's field errors (`{ email: "no_account" }`), for forms that name the cause. */
    readonly fields?: Record<string, string>,
  ) {
    super(code);
  }
}

/** The first field error's value, if the server sent one. */
export const fieldError = (err: unknown): string | undefined =>
  err instanceof ApiError && err.fields ? Object.values(err.fields)[0] : undefined;

/** Thrown to the caller when the staff member closes "Confirm it's you" (nothing was changed). */
export class StepUpCancelled extends Error {
  constructor() {
    super("stepup_cancelled");
    this.name = "StepUpCancelled";
  }
}

/**
 * Step-up (owner, 2026-11-05: saving a plan sent the owner to the sign-in page for the authenticator
 * code, and the editor's changes were gone on return): a call answered `401 auth.stepup_required`
 * asks through this handler, which opens the code popup over the page (stepUp.tsx), then the same
 * call is sent again once, with the same idempotency key. Without a handler (signed-out pages) the
 * error reaches the caller and App.tsx falls back to the sign-in step.
 */
type StepUpHandler = () => Promise<void>;
let stepUpHandler: StepUpHandler | null = null;
export function setStepUpHandler(h: StepUpHandler | null): void {
  stepUpHandler = h;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  idempotencyKey?: string,
  afterStepUp = false,
): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method,
      credentials: "same-origin",
      headers: {
        "X-Ady-Client": "1",
        Accept: "application/json",
        ...(body === undefined ? {} : { "Content-Type": "application/json" }),
        ...(idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {}),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, "network");
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const json: unknown = text ? JSON.parse(text) : undefined;
  if (!res.ok) {
    const e = (json as ErrorBody | undefined)?.error;
    if (
      res.status === 401 &&
      e?.code === "auth.stepup_required" &&
      stepUpHandler &&
      !afterStepUp &&
      !path.startsWith("/auth/")
    ) {
      await stepUpHandler(); // StepUpCancelled when the popup is closed
      return request<T>(method, path, body, idempotencyKey, true);
    }
    const retry = res.headers.get("Retry-After");
    throw new ApiError(
      res.status,
      e?.code ?? "internal",
      e?.request_id,
      retry ? Number(retry) : undefined,
      e?.fields,
    );
  }
  return json as T;
}

export const api = {
  me: () => request<StaffMe>("GET", "/me"),
  otpStart: (email: string) =>
    request<{ challenge_id: string; resend_after_s: number }>("POST", "/auth/otp/start", {
      channel: "email",
      value: email,
      purpose: "sign_in",
    }),
  otpVerify: (challenge_id: string, code: string) =>
    request<PlatformVerifyResponse>("POST", "/auth/otp/verify", { challenge_id, code }),
  totp: (code: string) => request<void>("POST", "/auth/mfa/totp", { code }),
  recoveryCode: (code: string) => request<void>("POST", "/auth/mfa/recovery", { code }),
  // D-081: first sign-in without an authenticator sets one up here.
  mfaEnroll: () => request<AuthenticatorEnrolment>("POST", "/auth/mfa/enroll", {}),
  mfaEnrollConfirm: (code: string) =>
    request<{ mfa_at: string; recovery_codes: string[] }>("POST", "/auth/mfa/enroll/confirm", {
      code,
    }),
  // D-081: My security (the signed-in staff member's own authenticator apps).
  authenticators: () => request<StaffAuthenticators>("GET", "/me/authenticators"),
  authenticatorAdd: (label: string) =>
    request<AuthenticatorEnrolment>("POST", "/me/authenticators", { label }),
  authenticatorConfirm: (code: string) =>
    request<{ id: string; recovery_codes?: string[] }>("POST", "/me/authenticators/confirm", {
      code,
    }),
  authenticatorRemove: (id: string) =>
    request<void>("DELETE", `/me/authenticators/${encodeURIComponent(id)}`),
  recoveryRegenerate: () => request<{ recovery_codes: string[] }>("POST", "/me/recovery-codes", {}),
  /** A full-page navigation: Google returns to /api/system/auth/google/callback on this origin. */
  googleStartUrl: `${API_BASE}/auth/google/start`,
  logout: () => request<void>("POST", "/auth/logout", {}),
  // ORG-02 KYC review (P6-C): reviewer role + MFA; decisions are idempotent.
  kycQueue: (state: KycQueueState = "submitted") =>
    request<{ data: KycQueueItem[] }>("GET", `/kyc?state=${state}`),
  kycCase: (caseId: string) => request<KycReview>("GET", `/kyc/${encodeURIComponent(caseId)}`),
  /** D-068: verify a shop without a complete review (the reason stays with staff). */
  kycForceVerify: (orgId: string, reason: string) =>
    request<KycReview>(
      "POST",
      `/kyc/shops/${encodeURIComponent(orgId)}/verify`,
      { reason },
      crypto.randomUUID(),
    ),
  /** D-068: take the tick away (the shop sees the reason). */
  kycRevoke: (orgId: string, reason: string) =>
    request<KycReview>(
      "POST",
      `/kyc/shops/${encodeURIComponent(orgId)}/revoke`,
      { reason },
      crypto.randomUUID(),
    ),
  /** D-068: a document (or `docId` "agreement", the signed copy), opened in the browser. */
  kycFileUrl: (caseId: string, docId: string) =>
    `${API_BASE}/kyc/${encodeURIComponent(caseId)}/files/${encodeURIComponent(docId)}`,
  kycAgreement: () => request<{ template: KycAgreementTemplate | null }>("GET", "/kyc/agreement"),
  kycAgreementFileUrl: `${API_BASE}/kyc/agreement/file`,
  /** The agreement PDF vendors download (raw PDF body, ≤ 10 MB). */
  kycAgreementUpload: async (file: File): Promise<{ template: KycAgreementTemplate | null }> => {
    let res: Response;
    try {
      res = await fetch(
        `${API_BASE}/kyc/agreement?filename=${encodeURIComponent(file.name.slice(0, 200))}`,
        {
          method: "PUT",
          credentials: "same-origin",
          headers: {
            "X-Ady-Client": "1",
            Accept: "application/json",
            "Content-Type": "application/pdf",
          },
          body: file,
        },
      );
    } catch {
      throw new ApiError(0, "network");
    }
    const text = await res.text();
    const json: unknown = text ? JSON.parse(text) : undefined;
    if (!res.ok) {
      const e = (json as ErrorBody | undefined)?.error;
      throw new ApiError(res.status, e?.code ?? "internal");
    }
    return json as { template: KycAgreementTemplate | null };
  },
  kycDecide: (caseId: string, decision: "approved" | "needs_info" | "rejected", note?: string) =>
    request<unknown>(
      "POST",
      `/kyc/${encodeURIComponent(caseId)}/decision`,
      { decision, ...(note ? { note } : {}) },
      crypto.randomUUID(),
    ),
};

/** SERVICES-001 Import Marketplace: a catalogue product as the console edits it. */
export type ImportProductInput = Omit<
  ImportProduct,
  "id" | "created_at" | "updated_at" | "version"
> & {
  version?: number;
};

const imp = (path: string) => `/import${path}`;
export const importApi = {
  board: () => request<ImportBoard>("GET", imp("/board")),
  createProduct: (body: ImportProductInput) =>
    request<ImportProduct>("POST", imp("/products"), body),
  updateProduct: (id: string, body: ImportProductInput & { version: number }) =>
    request<ImportProduct>("PUT", imp(`/products/${encodeURIComponent(id)}`), body),
  setState: (
    id: string,
    body: { state: ImportOrderState; note?: string; tracking?: string; eta?: string },
  ) => request<ImportOrder>("POST", imp(`/orders/${encodeURIComponent(id)}/state`), body),
  record: (
    id: string,
    body: { amount_paisa: number; method: ImportPayMethod; reference?: string },
  ) => request<ImportOrder>("POST", imp(`/orders/${encodeURIComponent(id)}/payments`), body),
  decide: (paymentId: string, body: { decision: "confirmed" | "rejected"; note?: string }) =>
    request<ImportOrder>("POST", imp(`/payments/${encodeURIComponent(paymentId)}/decision`), body),
  respondOffer: (
    id: string,
    body: {
      action: "accept" | "counter" | "decline";
      qty?: number;
      unit_price_paisa?: number;
      note?: string;
    },
  ) => request<{ ok: true }>("POST", imp(`/offers/${encodeURIComponent(id)}/respond`), body),
  respondRequest: (
    id: string,
    body: { action: "quote" | "decline"; quote?: ImportQuote; note?: string },
  ) => request<{ ok: true }>("POST", imp(`/requests/${encodeURIComponent(id)}/respond`), body),
  /** One catalogue photo, sent as-is; answers its file ref. */
  async upload(file: Blob): Promise<{ ref: string }> {
    const res = await fetch(`${API_BASE}/import/files`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "X-Ady-Client": "1", Accept: "application/json", "Content-Type": file.type },
      body: file,
    });
    if (!res.ok) throw new ApiError(res.status, "validation.invalid");
    return (await res.json()) as { ref: string };
  },
  /** A catalogue photo, or a vendor's file (their org is needed to find it). */
  fileUrl: (ref: string | null | undefined, orgId?: string) =>
    ref
      ? `${API_BASE}/import/files/${encodeURIComponent(ref)}${orgId && !ref.startsWith("p_") ? `?org=${encodeURIComponent(orgId)}` : ""}`
      : null,
};

/** BILLING-001 Adyvance Wallet (D-064): top-ups to check, wallets, plans, prices and settings. */
export const walletConsoleApi = {
  board: () => request<WalletBoard>("GET", "/wallet/board"),
  decide: (
    id: string,
    body: { decision: "confirmed" | "rejected"; amount_paisa?: number; note?: string },
  ) => request<{ ok: true }>("POST", `/wallet/topups/${encodeURIComponent(id)}/decision`, body),
  adjust: (body: { org_id: string; amount_paisa: number; note: string }) =>
    request<{ ok: true }>("POST", "/wallet/adjust", body),
  ledger: (orgId: string) =>
    request<{ data: WalletActivity[] }>("GET", `/wallet/orgs/${encodeURIComponent(orgId)}/ledger`),
  putSettings: (body: WalletBoard["settings"]) =>
    request<WalletBoard>("PUT", "/wallet/settings", body),
  putAiPacks: (packs: { id?: string; credits: number; price_paisa: number; active: boolean }[]) =>
    request<WalletBoard>("PUT", "/wallet/ai-packs", { packs }),
  putThemes: (
    themes: {
      key: string;
      price_paisa: number | null;
      period: "once" | "yearly";
      active: boolean;
    }[],
  ) => request<WalletBoard>("PUT", "/wallet/themes", { themes }),
  putAddons: (addons: { key: string; monthly_paisa: number | null }[]) =>
    request<WalletBoard>("PUT", "/wallet/addons", { addons }),
  // D-084: Adyvance's own eSewa / Khalti / Fonepay keys (owner only; values are write-only).
  putGateway: (
    provider: AdyGateway,
    body: { enabled: boolean; mode: "test" | "live"; credentials: Record<string, string> },
  ) => request<WalletBoard>("PUT", `/wallet/gateways/${provider}`, body),
  removeGateway: (provider: AdyGateway) =>
    request<WalletBoard>("POST", `/wallet/gateways/${provider}/remove`),
};

export interface PlanPutBody {
  name: string;
  summary: string | null;
  badge: string | null;
  icon: PlanIcon;
  active: boolean;
  trial_days: number | null;
  prices: { monthly?: number; yearly?: number };
  entitlements: { key: string; value: number }[];
}

/** BILLING-001 Plans & pricing (D-065): plans, prices, features, limits and monthly credits. */
export const plansConsoleApi = {
  board: () => request<PricingBoard>("GET", "/plans"),
  create: (body: { name: string; copy_from?: string }) =>
    request<{ id: string; board: PricingBoard }>("POST", "/plans", body),
  put: (id: string, body: PlanPutBody) =>
    request<PricingBoard>("PUT", `/plans/${encodeURIComponent(id)}`, body),
  order: (ids: string[]) => request<PricingBoard>("PUT", "/plan-order", { ids }),
  /** On sale or not, from the plan's card (owner, 2026-11-05). */
  sale: (id: string, active: boolean) =>
    request<PricingBoard>("PUT", `/plans/${encodeURIComponent(id)}/sale`, { active }),
  /** Only a plan no shop has ever had (the server says why otherwise). */
  remove: (id: string) => request<PricingBoard>("DELETE", `/plans/${encodeURIComponent(id)}`),
};

export interface OverrideBody {
  key: string;
  value: number;
  expires_at: string | null;
  note: string | null;
}
export interface NoticeBody {
  audience: "all" | "plan" | "org";
  plan_id?: string;
  org_id?: string;
  tone: PlatformNoticeView["tone"];
  title: string;
  body: string;
  link: string | null;
  pin_days: number;
}

/** SYSTEM-001 vendor control centre (D-065): overview, shops, lifecycle, plan, overrides, messages,
 * support access. */
export const vendorsConsoleApi = {
  overview: () => request<ConsoleOverview>("GET", "/overview"),
  directory: () => request<VendorDirectory>("GET", "/shops"),
  detail: (orgId: string) => request<VendorDetail>("GET", `/shops/${encodeURIComponent(orgId)}`),
  lifecycle: (orgId: string, body: { action: "suspend" | "reactivate"; reason: string }) =>
    request<{ ok: true }>("POST", `/shops/${encodeURIComponent(orgId)}/lifecycle`, body),
  plan: (
    orgId: string,
    body: { action: "give" | "extend" | "default"; plan_id?: string; days?: number; note: string },
  ) => request<VendorDetail>("POST", `/shops/${encodeURIComponent(orgId)}/plan`, body),
  overrides: (orgId: string, overrides: OverrideBody[]) =>
    request<VendorDetail>("PUT", `/shops/${encodeURIComponent(orgId)}/overrides`, { overrides }),
  notices: () => request<{ data: PlatformNoticeView[] }>("GET", "/notices"),
  sendNotice: (body: NoticeBody) => request<{ id: string }>("POST", "/notices", body),
  withdrawNotice: (id: string) =>
    request<{ ok: true }>("POST", `/notices/${encodeURIComponent(id)}/withdraw`),
  askAccess: (orgId: string, body: { reason: string; minutes: number }) =>
    request<{ id: string }>("POST", `/shops/${encodeURIComponent(orgId)}/access`, body),
  openAccess: (orgId: string, grantId: string) =>
    request<{ url: string }>(
      "POST",
      `/shops/${encodeURIComponent(orgId)}/access/${encodeURIComponent(grantId)}/open`,
    ),
  endAccess: (orgId: string, grantId: string) =>
    request<{ ok: true }>(
      "POST",
      `/shops/${encodeURIComponent(orgId)}/access/${encodeURIComponent(grantId)}/end`,
    ),
};

/** SUPPORT-001 help guides (existing console routes system.help.*). */
export interface HelpGuide {
  id: string;
  slug: string;
  title: string;
  body: string;
  module: string;
  video_url: string | null;
  published: boolean;
  updated_at: string | null;
}
export type HelpGuideBody = Omit<HelpGuide, "id" | "updated_at">;

export interface IdeaBody {
  title: string;
  body: string;
  status: IdeaStatus;
  public: boolean;
  response: string | null;
  registry_id: string | null;
}
export interface ReleaseBody {
  title: string;
  body: string;
  tag: ReleaseTag;
  link: string | null;
  feature_key: string | null;
  idea_ids: string[];
  published: boolean;
  notify_voters: boolean;
}

/** D-067 batch 1: the support desk, ideas, What's new and the onboarding tracker. */
export const deskConsoleApi = {
  board: (state: "active" | DeskState | "all") =>
    request<DeskBoard>("GET", `/desk/tickets?state=${state}`),
  ticket: (id: string) => request<DeskTicket>("GET", `/desk/tickets/${encodeURIComponent(id)}`),
  reply: (id: string, body: { body: string; internal: boolean; close: boolean }) =>
    request<DeskTicket>("POST", `/desk/tickets/${encodeURIComponent(id)}/messages`, body),
  update: (
    id: string,
    body: { state?: DeskState; priority?: DeskPriority; assignee_id?: string | null },
  ) => request<DeskTicket>("PUT", `/desk/tickets/${encodeURIComponent(id)}`, body),
  toIdea: (id: string) =>
    request<{ id: string }>("POST", `/desk/tickets/${encodeURIComponent(id)}/idea`),
  guides: () => request<{ data: HelpGuide[] }>("GET", "/help"),
  createGuide: (body: HelpGuideBody) => request<{ data: HelpGuide }>("POST", "/help", body),
  updateGuide: (id: string, body: HelpGuideBody) =>
    request<{ data: HelpGuide }>("PUT", `/help/${encodeURIComponent(id)}`, body),
  deleteGuide: (id: string) => request<null>("DELETE", `/help/${encodeURIComponent(id)}`),
  ideas: () => request<IdeaBoard>("GET", "/ideas"),
  createIdea: (body: IdeaBody) => request<{ id: string }>("POST", "/ideas", body),
  updateIdea: (id: string, body: IdeaBody) =>
    request<{ ok: true }>("PUT", `/ideas/${encodeURIComponent(id)}`, body),
  mergeIdea: (id: string, into: string) =>
    request<{ ok: true }>("POST", `/ideas/${encodeURIComponent(id)}/merge`, { into }),
  releases: () => request<{ data: ReleaseNoteView[] }>("GET", "/releases"),
  createRelease: (body: ReleaseBody) =>
    request<{ id: string; told: number }>("POST", "/releases", body),
  updateRelease: (id: string, body: ReleaseBody) =>
    request<{ id: string; told: number }>("PUT", `/releases/${encodeURIComponent(id)}`, body),
  onboarding: () => request<OnboardingBoard>("GET", "/onboarding"),
  nudge: (body: { org_ids: string[]; step: SetupStep; title: string; body: string }) =>
    request<{ sent: number }>("POST", "/onboarding/nudge", body),
};

/** D-067 batch 2: subscriptions, invoices, coupons, referrals and analytics. */
export const billingOpsApi = {
  subscriptions: () => request<SubscriptionsBoard>("GET", "/subscriptions"),
  remind: (subId: string) =>
    request<{ ok: true }>("POST", `/subscriptions/${encodeURIComponent(subId)}/remind`),
  putRules: (body: BillingRules) => request<BillingRules>("PUT", "/billing-rules", body),
  invoices: (month: string | null) =>
    request<InvoiceBoard>("GET", month ? `/invoices?month=${month}` : "/invoices"),
  invoice: (id: string) => request<InvoiceView>("GET", `/invoices/${encodeURIComponent(id)}`),
  coupons: () => request<{ data: PlanCouponView[] }>("GET", "/coupons"),
  createCoupon: (body: PlanCouponBody) => request<{ id: string }>("POST", "/coupons", body),
  updateCoupon: (id: string, body: PlanCouponBody) =>
    request<{ ok: true }>("PUT", `/coupons/${encodeURIComponent(id)}`, body),
  referrals: () => request<ReferralBoard>("GET", "/referrals"),
  putReferral: (body: ReferralSettings) =>
    request<ReferralSettings>("PUT", "/referral-settings", body),
  voidReferral: (id: string) =>
    request<{ ok: true }>("POST", `/referrals/${encodeURIComponent(id)}/void`),
  revenue: () => request<RevenueReport>("GET", "/analytics/revenue"),
  shops: () => request<ShopsReport>("GET", "/analytics/shops"),
};

/** D-093: a theme package as downloaded (the shape theme.json has; `preview` names its files). */
export interface ThemePackageFile {
  format: string;
  key: string;
  name: string;
  version: string;
  summary: string;
  document: Record<string, unknown>;
  preview?: {
    card?: string;
    images?: Record<string, string>;
    products?: {
      title: string;
      price_paisa: number;
      compare_at_paisa?: number;
      image?: string;
    }[];
  };
}

/** D-093: the address of a file of an uploaded theme version (`images/…`), any status. */
export const themeFileUrl = (key: string, version: string, path: string): string =>
  `${API_BASE}/themes/${encodeURIComponent(key)}/files/${path
    .split("/")
    .map(encodeURIComponent)
    .join("/")}?v=${encodeURIComponent(version)}`;

/** D-067 batch 3: switches, health, storefronts and the theme catalog. */
export const platformOpsApi = {
  switches: () => request<SwitchBoard>("GET", "/switches"),
  putSwitch: (key: string, body: SwitchRules & { note: string | null }) =>
    request<SwitchBoard>("PUT", `/switches/${encodeURIComponent(key)}`, body),
  health: () => request<HealthBoard>("GET", "/health"),
  runChecks: () => request<{ data: unknown[] }>("POST", "/status/checks/run"),
  createIncident: (body: { title: string; impact: "minor" | "major"; public_note?: string }) =>
    request<{ data: IncidentView }>("POST", "/incidents", body),
  updateIncident: (id: string, body: { state: IncidentView["state"]; public_note?: string }) =>
    request<{ data: IncidentView }>("PUT", `/incidents/${encodeURIComponent(id)}`, body),
  shopIntegrations: (orgId: string) =>
    request<ShopIntegrations>("GET", `/shops/${encodeURIComponent(orgId)}/integrations`),
  storefronts: () => request<StorefrontBoard>("GET", "/storefronts"),
  themes: () => request<ThemeCatalogBoard>("GET", "/themes"),
  putTheme: (
    key: string,
    body: {
      status: "draft" | "published" | "retired";
      price_paisa: number | null;
      period: "once" | "yearly";
      /** D-091: the version the status is for (uploaded themes); absent = the whole theme. */
      version?: string;
    },
  ) => request<ThemeCatalogBoard>("PUT", `/themes/${encodeURIComponent(key)}`, body),
  /** D-091: upload a theme package (checked again on the server) with its card picture (data URL). */
  uploadTheme: (body: { package: unknown; preview: string }) =>
    request<ThemeCatalogBoard>("POST", "/themes", body),
  /** D-093: upload a .zip theme package (theme.json + its photos), base64; checked on the server. */
  uploadThemeZip: (zipBase64: string) =>
    request<ThemeCatalogBoard>("POST", "/themes", { zip: zipBase64 }),
  /** D-093: delete a theme (never the default); its shops move to Default (D-114). */
  deleteTheme: (key: string) =>
    request<ThemeDeleteResult>("DELETE", `/themes/${encodeURIComponent(key)}`),
  /** D-093: bring back a deleted built-in theme. */
  restoreTheme: (key: string) =>
    request<ThemeCatalogBoard>("POST", `/themes/${encodeURIComponent(key)}/restore`),
  /** D-093: the console files route of an uploaded theme version (`images/…`). */
  themeFileUrl,
  /** D-091: a theme's package (first-party ones as a "my-<key>" starting point); D-093 with `preview`. */
  themePackage: (key: string, version?: string) =>
    request<ThemePackageFile>(
      "GET",
      `/themes/${encodeURIComponent(key)}/package${version ? `?v=${encodeURIComponent(version)}` : ""}`,
    ),
  grantTheme: (key: string, body: { org_id: string; days: number | null }) =>
    request<ThemeCatalogBoard>("POST", `/themes/${encodeURIComponent(key)}/grants`, body),
  revokeTheme: (key: string, orgId: string) =>
    request<ThemeCatalogBoard>(
      "POST",
      `/themes/${encodeURIComponent(key)}/grants/${encodeURIComponent(orgId)}/revoke`,
    ),
};

/** D-067 batch 4: the console team, the audit log, shoppers' reports with the storefront take-down,
 * legal documents, root AI usage and failed background jobs (SYSTEM-02). */
export const trustOpsApi = {
  team: () => request<TeamBoard>("GET", "/team"),
  addStaff: (body: { email: string; role: StaffRole }) => request<TeamBoard>("POST", "/team", body),
  putStaff: (userId: string, body: { role: StaffRole; active: boolean }) =>
    request<TeamBoard>("PUT", `/team/${encodeURIComponent(userId)}`, body),
  audit: (q: { action?: string; org?: string; before?: number | null }) => {
    const sp = new URLSearchParams();
    if (q.action) sp.set("action", q.action);
    if (q.org) sp.set("org", q.org);
    if (q.before) sp.set("before", String(q.before));
    const qs = sp.toString();
    return request<AuditPage>("GET", `/audit${qs ? `?${qs}` : ""}`);
  },
  reports: () => request<ReportBoard>("GET", "/reports"),
  putReport: (id: string, body: { state: ReportState; staff_note: string | null }) =>
    request<ReportBoard>("PUT", `/reports/${encodeURIComponent(id)}`, body),
  warn: (id: string, body: { title: string; body: string }) =>
    request<ReportBoard>("POST", `/reports/${encodeURIComponent(id)}/warn`, body),
  storefront: (orgId: string, body: { action: "pause" | "resume"; reason: string }) =>
    request<{ ok: true; paused: boolean }>(
      "POST",
      `/shops/${encodeURIComponent(orgId)}/storefront`,
      body,
    ),
  legal: () => request<LegalBoard>("GET", "/legal"),
  publishLegal: (body: {
    kind: LegalKind;
    version: string;
    url: string;
    sha256: string | null;
    summary: string | null;
    starts_at: string | null;
  }) => request<LegalBoard>("POST", "/legal", body),
  usage: () => request<UsageBoard>("GET", "/usage"),
  putAiCost: (paisa: number | null) =>
    request<UsageBoard>("PUT", "/usage-settings", { ai_cost_per_credit_paisa: paisa }),
  failures: (state: JobFailureView["state"]) =>
    request<{ data: JobFailureView[]; next_cursor: null }>("GET", `/jobs/failures?state=${state}`),
  retry: (id: string) =>
    request<{ id: string; state: string }>(
      "POST",
      `/jobs/failures/${encodeURIComponent(id)}/retry`,
    ),
  discard: (id: string, reason: string) =>
    request<{ id: string; state: string }>(
      "POST",
      `/jobs/failures/${encodeURIComponent(id)}/discard`,
      { reason },
    ),
};

/** NOTIFY-001 SMS Service (D-052): purchases, balances, packs and payment details. */
export const smsConsoleApi = {
  board: () => request<SmsBoard>("GET", "/sms/board"),
  decide: (id: string, body: { decision: "confirmed" | "rejected"; note?: string }) =>
    request<{ ok: true }>("POST", `/sms/purchases/${encodeURIComponent(id)}/decision`, body),
  adjust: (body: { org_id: string; credits: number; note: string }) =>
    request<{ ok: true }>("POST", "/sms/credits", body),
  putPacks: (packs: { id?: string; credits: number; price_paisa: number; active: boolean }[]) =>
    request<{ packs: SmsPack[] }>("PUT", "/sms/packs", { packs }),
  putPay: (body: SmsPayDetails) => request<SmsPayDetails>("PUT", "/sms/pay", body),
};

/** SERVICES-001 Digital Marketing ads (D-053; Meta and Google): connections, ads, results by hand,
 * top-ups, wallets and settings. */
export const adsConsoleApi = {
  board: () => request<AdsBoard>("GET", "/ads/board"),
  decideTopup: (id: string, body: { decision: "confirmed" | "rejected"; note?: string }) =>
    request<{ ok: true }>("POST", `/ads/topups/${encodeURIComponent(id)}/decision`, body),
  adjust: (body: { org_id: string; amount: number; note: string }) =>
    request<{ ok: true }>("POST", "/ads/wallets", body),
  connection: (body: {
    org_id: string;
    store_id: string;
    platform: AdsPlatform;
    state: "requested" | "connected" | "failed" | "removed";
    account_id?: string;
    name?: string;
    note?: string;
  }) => request<{ ok: true }>("POST", "/ads/connections", body),
  campaign: (
    id: string,
    body: {
      action: "approve" | "reject" | "pause" | "resume" | "end" | "link";
      external_id?: string;
      note?: string;
    },
  ) => request<{ ok: true }>("POST", `/ads/campaigns/${encodeURIComponent(id)}`, body),
  results: (id: string, days: AdsInsightDay[]) =>
    request<{ ok: true }>("PUT", `/ads/campaigns/${encodeURIComponent(id)}/results`, { days }),
  settings: (body: { settings: Record<AdsPlatform, AdsSettings>; pay: AdsPayDetails }) =>
    request<{ settings: Record<AdsPlatform, AdsSettings>; pay: AdsPayDetails }>(
      "PUT",
      "/ads/settings",
      body,
    ),
};

/** SERVICES-001 Courses (D-054): how-to video sections and videos. */
export const coursesConsoleApi = {
  board: () => request<CoursesBoard>("GET", "/courses/board"),
  sections: (sections: { id?: string; title: string }[]) =>
    request<CoursesBoard>("PUT", "/courses/sections", { sections }),
  add: (body: {
    link: string;
    section_id?: string | null;
    title?: string;
    description?: string;
    published?: boolean;
  }) => request<CoursesBoard>("POST", "/courses/videos", body),
  update: (
    id: string,
    body: {
      section_id?: string | null;
      title?: string;
      description?: string | null;
      published?: boolean;
    },
  ) => request<CoursesBoard>("PUT", `/courses/videos/${encodeURIComponent(id)}`, body),
  remove: (id: string) =>
    request<CoursesBoard>("DELETE", `/courses/videos/${encodeURIComponent(id)}`),
  order: (ids: string[]) => request<CoursesBoard>("PUT", "/courses/order", { ids }),
};

export type KycQueueState = "submitted" | "needs_info" | "approved" | "rejected";
export interface KycQueueItem {
  id: string;
  org_id: string;
  org_name: string;
  state: string;
  registered_name: string | null;
  submitted_at: string | null;
  /** Verified by a console force verify (D-068). */
  forced?: boolean;
}
export interface KycAgreementTemplate {
  id: string;
  filename: string;
  bytes: number;
  uploaded_at: string | null;
}
/** GET /kyc/{case}: the shop and its case (presentKyc: never the full account number). */
export interface KycReview {
  org: { id: string; name: string; owner?: { name: string | null; email: string | null } | null };
  kyc: {
    id: string;
    state: KycQueueState | "draft";
    entity_type: string | null;
    registered_name: string | null;
    pan_vat: string | null;
    registration_no: string | null;
    registered_address: string | null;
    business_phone: string | null;
    bank: {
      name: string | null;
      account_last4: string | null;
      account_name: string | null;
      branch: string | null;
    } | null;
    documents: { id: string; kind: string; filename: string; uploaded_at: string | null }[];
    agreement: { filename: string; uploaded_at: string | null } | null;
    review_note: string | null;
    submitted_at: string | null;
    decided_at: string | null;
  };
  /** Staff only: approved by force verify, and the console's reason. */
  review?: { forced: boolean; staff_note: string | null };
}

/**
 * Sign-in screen messages: email sign-in says truthfully what is unavailable (no email delivery
 * yet, or a failed bot check) instead of falling back to the generic error.
 */
export function signInErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === "dependency.activation_required") return t("console.emailUnavailable");
    if (err.code === "validation.turnstile_failed") return t("console.turnstileFailed");
  }
  return errorMessage(err);
}

export function errorMessage(err: unknown): string {
  if (err instanceof StepUpCancelled) return t("errors.stepUpCancelled");
  if (!(err instanceof ApiError)) return t("errors.generic");
  if (err.code === "network") return t("errors.network");
  if (err.code === "rate_limited")
    return t("auth.errors.rate_limited", { seconds: err.retryAfterS ?? 60 });
  if (err.code.startsWith("auth."))
    return i18n.tDynamic(`auth.errors.${err.code.slice(5)}`, "auth.errors.generic");
  if (err.code === "dependency.activation_required") return t("errors.activationRequired");
  if (err.code === "not_found") return t("errors.notFound");
  if (err.code === "validation.mfa_already_enrolled") return t("console.enroll.alreadyEnrolled");
  if (err.code === "validation.last_factor") return t("console.mySecurity.lastOne");
  if (err.code.startsWith("validation.")) return t("errors.validation");
  return t("errors.generic");
}
